'use client';

import { type AriaButtonOptions, useButton } from '@react-aria/button';
import { useRef } from 'react';

import { filterProps } from '../../../internal/utils/filterProps';
import { mergeProps } from '../../../utils/mergeProps';
import { LinkBase, type LinkProps } from './LinkBase';

// `component="button"` branch: renders a `<span role="button">` (the role +
// keyboard behaviour come from `useButton`'s `elementType: 'span'`), NOT a
// native `<button>`. This keeps the link's inline layout and text styling while
// the element gains genuine button semantics (Enter *and* Space activation).
const LinkAsButton = (props: LinkProps) => {
  const [localProps, buttonRaProps] = filterProps(props, ['useButton']);

  const { isDisabled } = props;

  const internalRef = useRef<HTMLSpanElement>(null);

  const { buttonProps, isPressed } = useButton(
    {
      ...(buttonRaProps as AriaButtonOptions<'span'>),
      elementType: 'span',
      isDisabled,
    },
    internalRef
  );

  const elementProps = mergeProps(localProps, buttonProps, {
    tabIndex: isDisabled ? -1 : undefined,
  });

  return (
    <LinkBase
      {...elementProps}
      Root="span"
      internalRef={internalRef}
      isPressed={isPressed}
      isDisabled={isDisabled}
    />
  );
};

LinkAsButton.displayName = 'LinkAsButton';

export { LinkAsButton };
